import { workerCourses } from '../data/workerCourses'
import { practicesStore } from './practicesStore'
import { getTrainingProduct } from '../utils/trainingCourses'
import { certificateFileName, courseExpiryDate } from '../utils/workers'
import { Practice } from '../types/practice'
import { WorkerCourse } from '../types/worker'

type Listener = () => void

export interface WorkerCourseEntry {
  workerId: number
  course: WorkerCourse
}

// Single training database of the Risorse umane. It collects:
// - DIRETTA: records entered by SEF in the worker module (also for past training)
// - DIRETTA MASSIVA: records imported from a file
// - INDIRETTA: records coming from the practices of the Formazione module
class WorkerTrainingStore {
  private own = new Map<number, WorkerCourse[]>()
  // Changes made on indirect records (attestato, avviso scadenza)
  private overrides: Record<string, Partial<WorkerCourse>> = {}
  private listeners = new Set<Listener>()
  private version = 0

  constructor() {
    practicesStore.subscribe(() => this.emit())
  }

  getVersion(): number {
    return this.version
  }

  getForWorker(workerId: number): WorkerCourse[] {
    const indirect = practicesStore
      .getAll()
      .flatMap((p) => indirectCourse(p, workerId))
      .map((c) => ({ ...c, ...this.overrides[c.id] }))
    return [...this.ownFor(workerId), ...indirect]
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  add(workerId: number, course: WorkerCourse) {
    this.addMany([{ workerId, course }])
  }

  addMany(entries: WorkerCourseEntry[]) {
    entries.forEach(({ workerId, course }) => {
      this.own.set(workerId, [...this.ownFor(workerId), course])
    })
    this.emit()
  }

  update(workerId: number, courseId: string, changes: Partial<WorkerCourse>) {
    const own = this.ownFor(workerId)
    if (own.some((c) => c.id === courseId)) {
      this.own.set(
        workerId,
        own.map((c) => (c.id === courseId ? { ...c, ...changes } : c)),
      )
    } else {
      this.overrides[courseId] = { ...this.overrides[courseId], ...changes }
    }
    this.emit()
  }

  // Sample past training, entered directly, for workers not edited yet
  private ownFor(workerId: number): WorkerCourse[] {
    if (!this.own.has(workerId)) {
      this.own.set(
        workerId,
        workerCourses.map((c) => ({ ...c, id: `${c.id}-w${workerId}`, source: 'diretta' })),
      )
    }
    return this.own.get(workerId)!
  }

  private emit() {
    this.version += 1
    this.listeners.forEach((l) => l())
  }
}

// A positive practice with a scheduled course populates the training of
// each attendee taken from the company's Risorse umane
function indirectCourse(p: Practice, workerId: number): WorkerCourse[] {
  if (p.status !== 'Positivo' || !p.courseStatus || !p.courseCode) return []
  if (!p.attendees?.some((a) => a.workerId === workerId)) return []
  const product = getTrainingProduct(p.productCode)
  const name = product?.name || p.productCode
  const done = p.courseStatus === 'Eseguito'
  const date = p.courseEndDate || p.courseStartDate || ''
  return [
    {
      id: `ind-${p.id}-w${workerId}`,
      name,
      productCode: p.productCode,
      firstDate: date,
      updateDate: '',
      expiryDate: done ? courseExpiryDate(p.productCode, date) : '',
      certificateName: p.certificates === 'Inviati' ? certificateFileName(name) : '',
      expiryAlert: true,
      source: 'indiretta',
      done,
      practiceId: p.id,
      courseCode: p.courseCode,
    },
  ]
}

export const workerTrainingStore = new WorkerTrainingStore()
